import { useCallback, useEffect, useMemo, useState } from "react";
import { GameButton } from "../components/GameButton";
import { VerifiedCreatorMark } from "../components/VerifiedCreatorMark";
import { hasSupabaseConfiguration, publicApi, supabase, type TerminalBoard, type TerminalRank } from "../lib/supabase";

type BoardView = "value" | "founder";
type BoardState = "loading" | "ready" | "guest" | "error";

const editionLabels: Record<TerminalRank["edition"], string> = {
  arcade: "Arcade",
  midnight: "Midnight",
  coastal: "Coastal",
  library: "Library Plate",
};

function exactNumber(value = "0") {
  const [whole, decimal] = value.split(".");
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return decimal ? `${grouped}.${decimal}` : grouped;
}

function progressFor(row: TerminalRank) {
  try {
    const required = BigInt(row.required ?? "0");
    const remaining = BigInt(row.remaining ?? required.toString());
    if (required <= 0n) return 0;
    const gathered = required > remaining ? required - remaining : 0n;
    return Number((gathered * 10_000n) / required) / 100;
  } catch {
    return 0;
  }
}

export function TerminalPage() {
  const [view, setView] = useState<BoardView>("founder");
  const [board, setBoard] = useState<TerminalBoard | null>(null);
  const [state, setState] = useState<BoardState>(hasSupabaseConfiguration ? "loading" : "error");
  const [message, setMessage] = useState(hasSupabaseConfiguration ? "" : "The Terminal cannot reach the island server.");
  const [playerID, setPlayerID] = useState<string | null>(null);

  const loadBoard = useCallback(async (userID: string | null) => {
    setPlayerID(userID);
    if (!supabase || !userID) {
      setBoard(null);
      setState(supabase ? "guest" : "error");
      return;
    }
    setState("loading");
    setMessage("");
    const profile = await supabase.from("profiles")
      .select("prototype_reset_version,account_status")
      .eq("id", userID)
      .single();
    if (profile.error || profile.data?.account_status !== "saved") {
      throw profile.error ?? new Error("Save this player account before boarding the Terminal.");
    }
    const snapshot = await publicApi.terminalBoard(profile.data.prototype_reset_version as number);
    setBoard(snapshot);
    setState("ready");
  }, []);

  useEffect(() => {
    if (!supabase) return;
    let active = true;
    const open = (userID: string | null) => {
      window.setTimeout(() => {
        if (!active) return;
        void loadBoard(userID).catch((error: unknown) => {
          if (!active) return;
          setState("error");
          setMessage(error instanceof Error ? error.message : "The departures board jammed.");
        });
      }, 0);
    };
    const { data: authListener } = supabase.auth.onAuthStateChange((_event, session) => open(session?.user.id ?? null));
    void supabase.auth.getSession().then(({ data, error }) => {
      if (!active) return;
      if (error) {
        setState("error");
        setMessage(error.message);
      } else {
        open(data.session?.user.id ?? null);
      }
    });
    return () => {
      active = false;
      authListener.subscription.unsubscribe();
    };
  }, [loadBoard]);

  const rows = useMemo(() => view === "founder" ? board?.approaching ?? [] : board?.scores ?? [], [board, view]);
  const checkedAt = board?.generated_at ? new Date(board.generated_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : null;

  return (
    <article className="terminal-page">
      <header className="terminal-hero">
        <p className="eyebrow">Departures · Delays · Dangerous ambition</p>
        <h1>Somebody gets to be first.</h1>
        <p>Every unclaimed Founder is a train leaving the station. The board knows who is closest. It refuses to say whether that is healthy.</p>
      </header>

      <section className="terminal-board" aria-labelledby="terminal-board-title">
        <div className="terminal-board__masthead">
          <div>
            <p className="eyebrow">Founder’s Terminal</p>
            <h2 id="terminal-board-title">The departures board</h2>
          </div>
          {checkedAt && <span>Board checked at {checkedAt}</span>}
        </div>

        <div className="terminal-tabs" role="tablist" aria-label="Terminal leaderboards">
          <button type="button" role="tab" aria-selected={view === "value"} className={view === "value" ? "is-active" : ""} onClick={() => setView("value")}>
            <strong>Hoard Value</strong><span>Considerable baggage</span>
          </button>
          <button type="button" role="tab" aria-selected={view === "founder"} className={view === "founder" ? "is-active" : ""} onClick={() => setView("founder")}>
            <strong>Closest to Founder</strong><span>Claim the first</span>
          </button>
        </div>

        {state === "loading" && <div className="terminal-state"><span className="terminal-flap-loader" aria-hidden="true">◆</span><p>Consulting the conductor…</p></div>}
        {state === "guest" && (
          <div className="terminal-state terminal-state--ticket">
            <p className="eyebrow">Ticket required</p>
            <h3>The board recognizes saved players.</h3>
            <p>Sign in to see the same server-backed Hoard Value and Founder race shown inside the game.</p>
            <GameButton to="/account" tone="sea">Sign in and board</GameButton>
          </div>
        )}
        {state === "error" && (
          <div className="terminal-state terminal-state--ticket">
            <p className="eyebrow">Service interruption</p>
            <h3>The board ate the timetable.</h3>
            <p>{message || "Try the platform again in a moment."}</p>
            {playerID && <button className="terminal-refresh" type="button" onClick={() => void loadBoard(playerID).catch((error: unknown) => { setState("error"); setMessage(error instanceof Error ? error.message : "The departures board jammed."); })}>Try again</button>}
          </div>
        )}

        {state === "ready" && (
          <>
            <div className="terminal-list" role="tabpanel">
              <div className="terminal-list__label"><span>{view === "founder" ? "The chasing pack" : "Heavy luggage"}</span><b>Top 100</b></div>
              {rows.length === 0 && <p className="terminal-empty">The platform is quiet. Enjoy it while it lasts.</p>}
              {rows.map((row, index) => view === "founder"
                ? <FounderRaceRow key={`${row.player_id}-${row.edition}`} row={row} rank={index + 1} />
                : <HoardValueRow key={`${row.player_id}-${row.edition}`} row={row} rank={index + 1} />)}
            </div>
            <TerminalRules view={view} />
          </>
        )}
      </section>
    </article>
  );
}

function HoardValueRow({ row, rank }: { row: TerminalRank; rank: number }) {
  return (
    <article className={`terminal-entry ${rank <= 3 ? "terminal-entry--podium" : ""}`}>
      <span className="terminal-entry__rank">{String(rank).padStart(2, "0")}</span>
      <div className="terminal-entry__player"><strong>{row.username}<VerifiedCreatorMark username={row.username} /></strong><span>{editionLabels[row.edition]}</span></div>
      <div className="terminal-entry__score"><strong>{exactNumber(row.score)}</strong><span>Hoard value</span></div>
    </article>
  );
}

function FounderRaceRow({ row, rank }: { row: TerminalRank; rank: number }) {
  const remaining = exactNumber(row.remaining);
  const packed = row.remaining === "0";
  const progress = progressFor(row);
  return (
    <article className={`terminal-entry terminal-entry--race ${rank === 1 ? "terminal-entry--leader" : ""}`}>
      <span className="terminal-entry__rank">{String(rank).padStart(2, "0")}</span>
      <div className="terminal-entry__player"><strong>{row.username}<VerifiedCreatorMark username={row.username} /></strong><span>{editionLabels[row.edition]} · Founder #{row.target ?? "?"}</span></div>
      <div className="terminal-entry__score"><strong>{packed ? "Packed" : remaining}</strong><span>{packed ? "Now comes the tricky bit" : "card points to go"}</span></div>
      <div className="terminal-progress" aria-label={packed ? "Target card value reached" : `${remaining} card points still needed`}><i style={{ width: `${progress}%` }} /><b style={{ left: `calc(${progress}% - 10px)` }}>◆</b></div>
    </article>
  );
}

function TerminalRules({ view }: { view: BoardView }) {
  return (
    <details className="terminal-rules">
      <summary>{view === "founder" ? "Before you gloat" : "A word from the conductor"}</summary>
      {view === "founder" ? (
        <div><p>Every unclaimed Founder is a chance to leave your name behind. Each player brings the edition giving them their best shot.</p><p>Build value with lower-numbered cards from that edition. Founders sit this one out—they have already signed the guest book.</p><p>A packed Hoard is not a finished forge. You still need matching cards and enough Play and Win Points, and somebody else can get there first.</p></div>
      ) : (
        <div><p>Every card adds weight to your Hoard: #1 is worth 1, #2 is worth 2, #3 is worth 4. Each step doubles the load.</p><p>Your strongest edition gets the spotlight. Founders, cards at home, and cards waiting for a buyer all ride along.</p><p>These points earn bragging rights. The ticket office will not accept them.</p></div>
      )}
    </details>
  );
}
